<template>
  <modal
    id="support-auth-modal"
    ref="modal"
    size="small"
    :show-close="true"
    @close="dismiss"
  >
    <template slot="title">Código para o suporte Kevi</template>
    <template slot="subtitle">Acesso temporário e somente leitura</template>
    <template slot="body">
      <div class="support-auth">
        <p class="support-auth__message">
          {{ message }}
        </p>

        <div
          class="support-auth__code-box"
          :class="{ 'is-urgent': isUrgent }"
          role="status"
          aria-live="polite"
          :aria-label="'Código: ' + rawCode"
        >
          <div class="support-auth__digits">
            <span
              v-for="(d, i) in digits"
              :key="'d-' + i"
              class="support-auth__digit-wrap"
            >
              <span class="support-auth__digit">{{ d }}</span>
              <span
                v-if="i === 2"
                class="support-auth__sep"
                aria-hidden="true"
              >·</span>
            </span>
          </div>
          <p v-if="countdownLabel" class="support-auth__timer">
            Expira em {{ countdownLabel }}
          </p>
        </div>

        <p class="support-auth__hint">
          Compartilhe este código só no chat oficial da Kevi. Não envie sua senha.
        </p>
      </div>
    </template>
    <template slot="footer">
      <kv-button
        variant="outlined"
        size="regular"
        class="support-auth__btn"
        @click="dismiss"
      >
        Já informei
      </kv-button>
      <kv-button
        variant="filled"
        size="regular"
        class="support-auth__btn"
        @click="copyCode"
      >
        {{ copied ? "Copiado" : "Copiar código" }}
      </kv-button>
    </template>
  </modal>
</template>

<script>
import keviApi from "../../keviApi";
import KvButton from "../form/kv-button.vue";

const DISMISS_KEY = "kevi.mkt.support_auth.dismissed";

export default {
  name: "support-auth-modal",
  components: {
    "kv-button": KvButton,
  },
  data() {
    return {
      live: null,
      copied: false,
      dismissedRequestId: "",
      nowMs: Date.now(),
      clockTimer: null,
      pollTimer: null,
    };
  },
  computed: {
    isLoggedIn() {
      return !!(this.$store && this.$store.state.user && this.$store.state.user.token);
    },
    rawCode() {
      return String((this.live && this.live.code) || "").replace(/\s/g, "");
    },
    digits() {
      return this.rawCode.padStart(6, "0").slice(0, 6).split("");
    },
    remainingSec() {
      const exp = this.live && this.live.code_expires_at;
      if (exp == null || exp === "") return null;
      let expMs = typeof exp === "number" ? exp : Date.parse(exp);
      if (!expMs && /^\d+$/.test(String(exp))) {
        expMs = Number(exp);
      }
      if (!expMs) return null;
      if (expMs < 1e12) {
        expMs *= 1000;
      }
      return Math.max(0, Math.floor((expMs - this.nowMs) / 1000));
    },
    countdownLabel() {
      const sec = this.remainingSec;
      if (sec == null) return "";
      const m = Math.floor(sec / 60);
      const s = sec % 60;
      return m + ":" + String(s).padStart(2, "0");
    },
    isUrgent() {
      return this.remainingSec != null && this.remainingSec <= 60;
    },
    message() {
      return (
        (this.live && this.live.message) ||
        "O suporte solicitou acesso limitado à sua conta para ajudar neste atendimento."
      );
    },
    hasPendingCode() {
      if (!this.live || this.live.status !== "pending_code" || !this.rawCode) {
        return false;
      }
      if (this.live.request_id && this.live.request_id === this.dismissedRequestId) {
        return false;
      }
      return true;
    },
  },
  created() {
    try {
      this.dismissedRequestId = sessionStorage.getItem(DISMISS_KEY) || "";
    } catch (e) {
      this.dismissedRequestId = "";
    }
  },
  mounted() {
    this.loadLive();
    this.clockTimer = setInterval(() => {
      this.nowMs = Date.now();
    }, 1000);
    this.pollTimer = setInterval(() => {
      if (this.isLoggedIn && !this.hasPendingCode) {
        this.loadLive();
      }
    }, 8000);
    document.addEventListener("visibilitychange", this.onPageResume);
    window.addEventListener("focus", this.onPageResume);
  },
  watch: {
    isLoggedIn(val) {
      if (val) {
        this.loadLive();
      } else {
        this.live = null;
        this.closeModal();
      }
    },
  },
  beforeDestroy() {
    if (this.clockTimer) clearInterval(this.clockTimer);
    if (this.pollTimer) clearInterval(this.pollTimer);
    document.removeEventListener("visibilitychange", this.onPageResume);
    window.removeEventListener("focus", this.onPageResume);
  },
  methods: {
    onPageResume() {
      if (document.visibilityState && document.visibilityState !== "visible") {
        return;
      }
      this.loadLive();
    },
    extractLive(res) {
      let body = (res && (res.body !== undefined ? res.body : res.data)) || {};
      if (typeof body === "string") {
        try {
          body = JSON.parse(body);
        } catch (e) {
          body = {};
        }
      }
      if (body && body.live) {
        return body.live;
      }
      if (body && body.status === "pending_code" && body.code) {
        return body;
      }
      return null;
    },
    async loadLive() {
      if (!this.isLoggedIn) {
        this.live = null;
        this.closeModal();
        return;
      }
      try {
        const opts = keviApi.getOptions("support/auth/active", "GET");
        const res = await this.$http(opts);
        this.live = this.extractLive(res);
        this.$nextTick(() => {
          if (this.hasPendingCode) {
            this.openModal();
          } else {
            this.closeModal();
          }
        });
      } catch (e) {
        this.live = null;
        this.closeModal();
        if (process.env.NODE_ENV !== "production") {
          console.warn("[support-auth] GET support/auth/active failed", e);
        }
      }
    },
    openModal() {
      const modal = this.$refs.modal;
      if (modal && typeof modal.open === "function") {
        modal.open();
      }
    },
    closeModal() {
      const modal = this.$refs.modal;
      if (modal && typeof modal.close === "function" && modal.isOpen) {
        modal.close();
      }
    },
    dismiss() {
      try {
        if (this.live && this.live.request_id) {
          sessionStorage.setItem(DISMISS_KEY, this.live.request_id);
          this.dismissedRequestId = this.live.request_id;
        }
      } catch (e) {
        if (this.live && this.live.request_id) {
          this.dismissedRequestId = this.live.request_id;
        }
      }
      this.closeModal();
    },
    async copyCode() {
      try {
        await navigator.clipboard.writeText(this.rawCode);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (e) {
        /* ignore */
      }
    },
  },
};
</script>

<style scoped lang="less">
.support-auth {
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;
}

.support-auth__message {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #6b7280;
}

.support-auth__code-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #f9fafb;
}

.support-auth__code-box.is-urgent {
  border-color: #fecaca;
  background: #fef2f2;
}

.support-auth__digits {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.support-auth__digit-wrap {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.support-auth__digit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 52px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
}

.support-auth__code-box.is-urgent .support-auth__digit {
  border-color: #fecaca;
}

.support-auth__sep {
  padding: 0 2px;
  font-size: 20px;
  font-weight: 300;
  color: #d1d5db;
}

.support-auth__timer {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}

.support-auth__code-box.is-urgent .support-auth__timer {
  color: #dc2626;
}

.support-auth__hint {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: #9ca3af;
}

.support-auth__btn {
  min-width: 140px;
}

@media (max-width: 768px) {
  .support-auth__digit {
    width: 36px;
    height: 48px;
    font-size: 22px;
  }
}
</style>

<style>
#support-auth-modal.kv-modal {
  z-index: 20000 !important;
}
</style>
